import { create } from 'zustand';

import { closeOtherDrawers, registerDrawerCloser } from './_drawer-mutex';

import { useInspector } from '@/components/ui/inspector/hooks/useInspector';
import type { InspectorBreadcrumbItem } from '@/components/ui/inspector/types';
import type { AssetSource } from '@/lib/media-studio/route-for-source';
import { registerWorkspaceReset } from '@/lib/workspace/workspaceResetRegistry';

export interface VideoDialogPayload {
  jobId: string;
  videoUrl: string;
  posterUrl?: string | null;
  prompt?: string;
  model?: string;
  duration?: number;
  aspectRatio?: string;
  seed?: string | number;
  createdAt?: string | Date;
  title?: string;
  source?: AssetSource;
  thumbnailUrl?: string;
  breadcrumbs?: InspectorBreadcrumbItem[];
}

interface VideoDialogState {
  isOpen: boolean;
  payload: VideoDialogPayload | null;
  open: (payload: VideoDialogPayload) => void;
  close: () => void;
}

export const useVideoDialogStore = create<VideoDialogState>((set) => ({
  isOpen: false,
  payload: null,
  open: (payload) => {
    closeOtherDrawers('video');
    set({ isOpen: true, payload });
    const inspectorOptions = payload.breadcrumbs
      ? { presentation: 'workspace' as const, breadcrumbs: payload.breadcrumbs }
      : { presentation: 'workspace' as const };
    useInspector
      .getState()
      .openSubject(
        { kind: 'video', mediaId: payload.jobId, jobId: payload.jobId },
        inspectorOptions,
      );
  },
  close: () => {
    set({ isOpen: false });
    const inspector = useInspector.getState();
    const top = inspector.stack[inspector.stack.length - 1];
    if (top?.kind === 'video') inspector.close();
  },
}));

registerDrawerCloser('video', () => useVideoDialogStore.getState().close());

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as unknown as { __videoDialogStore: typeof useVideoDialogStore }).__videoDialogStore =
    useVideoDialogStore;
}

// close() intentionally leaves `isOpen: false` only, keeping `payload` around
// (some callers read it mid-close-animation). That is a problem specifically
// across a workspace switch: an open video dialog carries a REAL job's
// payload from the workspace being left, and — unlike a query-backed list —
// nothing else clears it. Force both flags off here so no stale payload can
// render, or be reopened from, the previous workspace.
registerWorkspaceReset('video-dialog-store', () => {
  useVideoDialogStore.setState({ isOpen: false, payload: null });
});
